import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GitBranch, Boxes, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Trash2,
  ExternalLink, UserPlus, Lock, Unlock, ChevronDown, ChevronRight, ShieldCheck,
  HardDrive, Eye, Play,
} from 'lucide-react';
import {
  forgejo, registryPflege, servers as serversApi,
  type ForgejoKontoZeile, type PflegeBericht, type MuellBericht, type Staffelung,
} from '../api';

/**
 * Git und Registry an einer Stelle.
 *
 * ══ WARUM DIE BEIDEN ZUSAMMEN ══════════════════════════════════════════════
 *
 * Weil es dieselbe Maschine ist und dasselbe Problem: was wir bauen, landet
 * als Abzug in der Registry, und was wir schreiben, liegt im Git daneben. Wer
 * hier ist, will entweder wissen, wer Zugang hat, oder warum die Platte voll
 * ist. Zwei Seiten dafür wären zwei Orte zum Vergessen.
 *
 * ══ WAS DIE SEITE NICHT IST ════════════════════════════════════════════════
 *
 * Kein Ersatz für Forgejo. Repositories, Pull Requests, Pipelines — das alles
 * bleibt dort, und der Knopf oben rechts führt hin. Hier steht nur, was der PC
 * besser weiß als Forgejo: wer im Haus ist, wer gehen sollte, und welche
 * Abzüge noch gebraucht werden.
 */

// ─── Hilfen ────────────────────────────────────────────────────────────────

function bytes(n: number | undefined): string {
  if (!n) return '—';
  const einheiten = ['B', 'KB', 'MB', 'GB', 'TB'];
  let wert = n;
  let i = 0;
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024;
    i += 1;
  }
  return `${wert.toFixed(wert < 10 && i > 0 ? 1 : 0)} ${einheiten[i]}`;
}

function datum(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function Meldung({ art, text }: { art: 'gut' | 'warnung' | 'fehler'; text: string }) {
  const stil = {
    gut: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    warnung: 'border-amber-200 bg-amber-50 text-amber-800',
    fehler: 'border-rose-200 bg-rose-50 text-rose-800',
  }[art];
  const Symbol = { gut: CheckCircle2, warnung: AlertTriangle, fehler: XCircle }[art];
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${stil}`}>
      <Symbol size={14} className="mt-px shrink-0" />
      <span>{text}</span>
    </div>
  );
}

// ─── Git ───────────────────────────────────────────────────────────────────

function GitBereich() {
  const qc = useQueryClient();
  const [rueckmeldung, setRueckmeldung] = useState<{ art: 'gut' | 'fehler'; text: string } | null>(null);

  const status = useQuery({ queryKey: ['forgejo', 'status'], queryFn: forgejo.status });
  const konten = useQuery({
    queryKey: ['forgejo', 'konten'],
    queryFn: forgejo.konten,
    enabled: status.data?.eingerichtet === true,
  });

  const nachAktion = (e: { ok: boolean; meldung: string }) => {
    setRueckmeldung({ art: e.ok ? 'gut' : 'fehler', text: e.meldung });
    qc.invalidateQueries({ queryKey: ['forgejo'] });
  };

  const anlegen = useMutation({ mutationFn: forgejo.anlegen, onSuccess: nachAktion });
  const sperren = useMutation({ mutationFn: forgejo.sperren, onSuccess: nachAktion });
  const entsperren = useMutation({ mutationFn: forgejo.entsperren, onSuccess: nachAktion });
  const abgleich = useMutation({ mutationFn: forgejo.abgleich, onSuccess: nachAktion });

  const laeuft =
    anlegen.isPending || sperren.isPending || entsperren.isPending || abgleich.isPending;

  if (status.isLoading) {
    return <p className="text-xs text-slate-500">Wird geladen …</p>;
  }

  if (!status.data?.eingerichtet) {
    return (
      <div className="space-y-3">
        <Meldung art="warnung" text={status.data?.meldung ?? 'Forgejo ist nicht eingerichtet.'} />
        <p className="text-xs text-slate-500">
          Unter Einstellungen in der Gruppe „Forgejo" gehören Adresse und ein Administrator-Token
          hinterlegt. Bis dahin ändert der PC an Git-Konten nichts — auch nicht versehentlich.
        </p>
      </div>
    );
  }

  const zeilen = konten.data?.zeilen ?? [];
  const abweichungen = konten.data?.abweichungen ?? 0;

  return (
    <div className="space-y-4">
      {/* Status */}
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Instanz</div>
          <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {status.data.erreichbar ? (
              <CheckCircle2 size={14} className="text-emerald-600" />
            ) : (
              <XCircle size={14} className="text-rose-600" />
            )}
            {status.data.erreichbar ? 'erreichbar' : 'nicht erreichbar'}
          </div>
          {status.data.adresse && (
            <a
              href={status.data.adresse}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-brand-600 hover:underline"
            >
              {status.data.adresse.replace(/^https?:\/\//, '')} <ExternalLink size={10} />
            </a>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Konten</div>
          <div className="mt-1 text-sm font-semibold text-slate-800">{status.data.konten.gesamt}</div>
          <div className="mt-1 text-[11px] text-slate-500">
            {status.data.konten.aktiv} aktiv, {status.data.konten.gesperrt} gesperrt
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Abweichungen</div>
          <div
            className={`mt-1 text-sm font-semibold ${
              abweichungen > 0 ? 'text-amber-700' : 'text-emerald-700'
            }`}
          >
            {abweichungen}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">PC gegen Git</div>
        </div>
        <div className="flex items-center rounded-xl border border-slate-200 bg-surface p-4">
          <button
            onClick={() => abgleich.mutate()}
            disabled={laeuft || abweichungen === 0}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-40"
          >
            <RefreshCw size={13} className={abgleich.isPending ? 'animate-spin' : ''} />
            Alles geradeziehen
          </button>
        </div>
      </div>

      {rueckmeldung && (
        <Meldung art={rueckmeldung.art === 'gut' ? 'gut' : 'fehler'} text={rueckmeldung.text} />
      )}

      {abgleich.data && abgleich.data.ergebnisse.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <h3 className="text-xs font-semibold text-slate-700">Was der Abgleich getan hat</h3>
          <ul className="mt-2 space-y-1">
            {abgleich.data.ergebnisse.map((e) => (
              <li key={e.email} className="flex items-center gap-2 text-[11px]">
                {e.ok ? (
                  <CheckCircle2 size={11} className="text-emerald-600" />
                ) : (
                  <XCircle size={11} className="text-rose-600" />
                )}
                <span className="font-medium text-slate-700">{e.email}</span>
                <span className="text-slate-500">{e.tat}</span>
                {!e.ok && <span className="text-rose-600">— {e.meldung}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Konten */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-surface">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] text-slate-600">
              <tr>
                <th className="px-4 py-2 font-medium">Person</th>
                <th className="px-4 py-2 font-medium">Im PC</th>
                <th className="px-4 py-2 font-medium">Git-Konto</th>
                <th className="px-4 py-2 font-medium">Schlüssel</th>
                <th className="px-4 py-2 font-medium">Abweichung</th>
                <th className="px-4 py-2 text-right font-medium">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {zeilen.map((z: ForgejoKontoZeile) => (
                <tr key={z.userId} className={z.abweichung ? 'bg-amber-50/40' : undefined}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-800">{z.name}</div>
                    <div className="text-[11px] text-slate-500">{z.email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        z.pcStatus === 'ACTIVE'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {z.pcStatus === 'ACTIVE' ? 'aktiv' : z.pcStatus.toLowerCase()}
                    </span>
                    <div className="mt-0.5 text-[10px] text-slate-400">{z.rolle}</div>
                  </td>
                  <td className="px-4 py-2">
                    {z.konto ? (
                      <>
                        <div className="font-mono text-[11px] text-slate-700">
                          {z.konto.benutzername}
                        </div>
                        <span
                          className={`text-[10px] ${
                            z.konto.aktiv ? 'text-emerald-600' : 'text-rose-600'
                          }`}
                        >
                          {z.konto.aktiv ? 'offen' : 'gesperrt'}
                        </span>
                      </>
                    ) : (
                      <span className="text-[11px] text-slate-400">keines</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {z.konto?.schluessel ? (
                      <span
                        title={z.konto.schluessel}
                        className="inline-flex items-center gap-1 text-[10px] text-emerald-700"
                      >
                        <ShieldCheck size={11} /> hinterlegt
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {z.abweichung ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
                        <AlertTriangle size={11} /> {z.abweichung}
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {!z.konto && z.pcStatus === 'ACTIVE' && (
                        <button
                          onClick={() => anlegen.mutate(z.userId)}
                          disabled={laeuft}
                          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        >
                          <UserPlus size={10} /> anlegen
                        </button>
                      )}
                      {z.konto?.aktiv && (
                        <button
                          onClick={() => sperren.mutate(z.userId)}
                          disabled={laeuft}
                          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        >
                          <Lock size={10} /> sperren
                        </button>
                      )}
                      {z.konto && !z.konto.aktiv && (
                        <button
                          onClick={() => entsperren.mutate(z.userId)}
                          disabled={laeuft}
                          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        >
                          <Unlock size={10} /> entsperren
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {zeilen.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-xs text-slate-400">
                    {konten.isLoading ? 'Wird geladen …' : 'Keine Nutzer.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-slate-500">
        Konten werden gesperrt, nicht gelöscht. Ein gelöschtes Konto nimmt seine Beiträge nicht
        mit, aber die Zuordnung der Commits zu einem Menschen wird unleserlich — und die braucht
        man Jahre später noch.
      </p>
    </div>
  );
}

// ─── Registry ──────────────────────────────────────────────────────────────

function RegistryBereich() {
  const [regeln, setRegeln] = useState<Staffelung>({
    alleTage: 14,
    taeglichTage: 30,
    woechentlichTage: 182,
  });
  const [bericht, setBericht] = useState<PflegeBericht | null>(null);
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [muell, setMuell] = useState<MuellBericht | null>(null);
  const [muellServer, setMuellServer] = useState('');
  const [rueckmeldung, setRueckmeldung] = useState<{ art: 'gut' | 'fehler'; text: string } | null>(null);

  const status = useQuery({ queryKey: ['registry', 'status'], queryFn: registryPflege.status });
  const serverListe = useQuery({ queryKey: ['servers'], queryFn: () => serversApi.list() });

  const vorschau = useMutation({
    mutationFn: () => registryPflege.vorschau(regeln),
    onSuccess: (b) => {
      setBericht(b);
      setRueckmeldung(null);
    },
    onError: (e: Error) => setRueckmeldung({ art: 'fehler', text: e.message }),
  });

  const ausfuehren = useMutation({
    mutationFn: () => registryPflege.ausfuehren(regeln),
    onSuccess: (e) => {
      setRueckmeldung({
        art: e.fehlgeschlagen === 0 ? 'gut' : 'fehler',
        text: `${e.geloescht} Verweise entfernt${
          e.fehlgeschlagen ? `, ${e.fehlgeschlagen} fehlgeschlagen` : ''
        }. ${e.hinweis}`,
      });
      setBericht(null);
    },
    onError: (e: Error) => setRueckmeldung({ art: 'fehler', text: e.message }),
  });

  const muellLauf = useMutation({
    mutationFn: (nurVorschau: boolean) => registryPflege.muell(muellServer, nurVorschau),
    onSuccess: (b) => setMuell(b),
    onError: (e: Error) => setRueckmeldung({ art: 'fehler', text: e.message }),
  });

  const laeuft = vorschau.isPending || ausfuehren.isPending || muellLauf.isPending;

  const umschalten = (repo: string) => {
    setOffen((alt) => {
      const neu = new Set(alt);
      if (neu.has(repo)) neu.delete(repo);
      else neu.add(repo);
      return neu;
    });
  };

  if (!status.data?.configured) {
    return (
      <Meldung
        art="warnung"
        text={status.data?.message ?? 'Es ist keine Registry hinterlegt (REGISTRY_URL).'}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Registry</div>
          <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {status.data.reachable ? (
              <CheckCircle2 size={14} className="text-emerald-600" />
            ) : (
              <XCircle size={14} className="text-rose-600" />
            )}
            {status.data.url ?? '—'}
          </div>
          {status.data.message && (
            <div className="mt-1 text-[11px] text-amber-700">{status.data.message}</div>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-surface p-4 sm:col-span-2">
          <div className="text-[11px] font-medium text-slate-500">Aufbewahrung</div>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            {(
              [
                ['alleTage', 'jeder Abzug bis', 'Tage'],
                ['taeglichTage', 'einer je Tag bis', 'Tage'],
                ['woechentlichTage', 'einer je Woche bis', 'Tage'],
              ] as Array<[keyof Staffelung, string, string]>
            ).map(([schluessel, beschriftung, einheit]) => (
              <label key={schluessel} className="block">
                <span className="block text-[10px] text-slate-500">{beschriftung}</span>
                <span className="mt-0.5 flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    value={regeln[schluessel]}
                    onChange={(e) =>
                      setRegeln({ ...regeln, [schluessel]: Number(e.target.value) || 0 })
                    }
                    className="w-20 rounded-lg border border-slate-300 px-2 py-1 text-xs tabular-nums"
                  />
                  <span className="text-[10px] text-slate-400">{einheit}</span>
                </span>
              </label>
            ))}
            <button
              onClick={() => vorschau.mutate()}
              disabled={laeuft}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <Eye size={13} className={vorschau.isPending ? 'animate-pulse' : ''} />
              {vorschau.isPending ? 'zählt …' : 'Vorschau'}
            </button>
          </div>
          <p className="mt-2 text-[10px] text-slate-500">
            Alles darüber wird entfernt. Was in einem Release steht und <code>latest</code> bleibt
            immer — unabhängig vom Alter.
          </p>
        </div>
      </div>

      {rueckmeldung && <Meldung art={rueckmeldung.art} text={rueckmeldung.text} />}

      {/* Bericht */}
      {bericht && (
        <div className="rounded-xl border border-slate-200 bg-surface">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
            <div className="flex flex-wrap items-center gap-4 text-xs">
              <span>
                <strong className="text-emerald-700 tabular-nums">{bericht.behalten}</strong>
                <span className="text-slate-500"> bleiben</span>
              </span>
              <span>
                <strong className="text-rose-700 tabular-nums">{bericht.loeschen}</strong>
                <span className="text-slate-500"> gehen</span>
              </span>
              <span
                title={
                  'Summe der Abzugsgrößen. Nicht der Platz, der frei wird — geteilte Schichten '
                  + 'zählen hier je Abzug voll mit, liegen im Speicher aber nur einmal. Was '
                  + 'wirklich frei wird, zeigt die Müllabfuhr.'
                }
                className="cursor-help border-b border-dotted border-slate-300"
              >
                <strong className="tabular-nums text-slate-700">{bytes(bericht.summeBytes)}</strong>
                <span className="text-slate-500"> Abzugsgröße</span>
              </span>
              <span className="text-[11px] text-slate-400">
                {bericht.geschuetzt.length} Tags geschützt
              </span>
            </div>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `${bericht.loeschen} Abzüge werden aus der Registry entfernt.\n\n` +
                      'Zurückholen geht nur durch einen neuen Build — bei einem Stand von vor ' +
                      'Monaten heißt das: er ist weg.\n\nFortfahren?',
                  )
                ) {
                  ausfuehren.mutate();
                }
              }}
              disabled={laeuft || bericht.loeschen === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            >
              <Trash2 size={13} />
              {ausfuehren.isPending ? 'löscht …' : `${bericht.loeschen} entfernen`}
            </button>
          </div>

          <div className="divide-y divide-slate-100">
            {bericht.repositories.map((r) => (
              <div key={r.repository}>
                <button
                  onClick={() => umschalten(r.repository)}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-slate-50"
                >
                  {offen.has(r.repository) ? (
                    <ChevronDown size={13} className="text-slate-400" />
                  ) : (
                    <ChevronRight size={13} className="text-slate-400" />
                  )}
                  <span className="flex-1 font-mono text-[11px] text-slate-700">{r.repository}</span>
                  <span className="text-[11px] text-emerald-700 tabular-nums">{r.behalten}</span>
                  <span className="text-[11px] text-slate-300">/</span>
                  <span className="text-[11px] text-rose-700 tabular-nums">{r.loeschen}</span>
                  <span className="w-16 text-right text-[11px] text-slate-500 tabular-nums">
                    {bytes(r.summeBytes)}
                  </span>
                </button>
                {offen.has(r.repository) && (
                  <div className="max-h-72 overflow-y-auto border-t border-slate-100 bg-slate-50/60 px-4 py-2">
                    <table className="w-full text-left text-[11px]">
                      <tbody className="divide-y divide-slate-200/60">
                        {r.tags.map((t) => (
                          <tr key={t.tag} className={t.behalten ? undefined : 'text-rose-700'}>
                            <td className="py-1 pr-3 font-mono">{t.tag}</td>
                            <td className="py-1 pr-3 text-slate-500 tabular-nums">
                              {datum(t.erstellt)}
                            </td>
                            <td className="py-1 pr-3 text-slate-500 tabular-nums">
                              {bytes(t.groesseBytes)}
                            </td>
                            <td className="py-1 text-slate-500">{t.grund}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>

          {bericht.fehler.length > 0 && (
            <div className="border-t border-slate-200 px-4 py-2">
              <details>
                <summary className="cursor-pointer text-[11px] text-amber-700">
                  {bericht.fehler.length} Abzüge liessen sich nicht lesen
                </summary>
                <ul className="mt-1 space-y-0.5 text-[10px] text-slate-500">
                  {bericht.fehler.slice(0, 40).map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Müllabfuhr */}
      <div className="rounded-xl border border-slate-200 bg-surface p-4">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <HardDrive size={14} /> Müllabfuhr
        </h3>
        <p className="mt-1 text-[11px] text-slate-500">
          Das Löschen oben nimmt nur die Verweise weg. Platz wird erst hier frei. Die Registry
          steht dafür etwa eine Minute still — in der Zeit kann kein Build pushen.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="block text-[10px] text-slate-500">Wo läuft die Registry?</span>
            <select
              value={muellServer}
              onChange={(e) => setMuellServer(e.target.value)}
              className="mt-0.5 rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
            >
              <option value="">— Server wählen —</option>
              {(serverListe.data ?? []).map((s: { id: string; hostname: string }) => (
                <option key={s.id} value={s.id}>
                  {s.hostname}
                </option>
              ))}
            </select>
          </label>
          <button
            onClick={() => muellLauf.mutate(true)}
            disabled={laeuft || !muellServer}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <Eye size={13} /> Probelauf
          </button>
          <button
            onClick={() => {
              if (
                window.confirm(
                  'Die Registry wird dafür angehalten und danach wieder gestartet.\n\n' +
                    'Läuft gerade ein Build, der pushen will, schlägt er fehl.\n\nFortfahren?',
                )
              ) {
                muellLauf.mutate(false);
              }
            }}
            disabled={laeuft || !muellServer}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          >
            <Play size={13} />
            {muellLauf.isPending ? 'läuft …' : 'Ausführen'}
          </button>
        </div>

        {muell && (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap gap-4 text-[11px]">
              <span className="text-slate-500">
                Container: <span className="font-mono text-slate-700">{muell.container ?? '—'}</span>
              </span>
              {muell.freigegeben && (
                <span className="text-emerald-700">frei: {muell.freigegeben}</span>
              )}
              {muell.nurVorschau && <span className="text-amber-700">nur Probelauf</span>}
            </div>
            <pre className="max-h-60 overflow-auto rounded-lg bg-slate-900 p-3 text-[10px] leading-relaxed text-slate-200">
              {muell.ausgabe}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Seite ─────────────────────────────────────────────────────────────────

export default function GitRegistryPage() {
  const [reiter, setReiter] = useState<'git' | 'registry'>('git');
  const status = useQuery({ queryKey: ['forgejo', 'status'], queryFn: forgejo.status });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Git &amp; Registry</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Wer Zugang zum Quelltext hat, und was an Abzügen liegenbleibt. Beides hängt an
            derselben Maschine.
          </p>
        </div>
        {status.data?.adresse && (
          <a
            href={status.data.adresse}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Forgejo öffnen <ExternalLink size={12} />
          </a>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {(
          [
            ['git', 'Git-Konten', GitBranch],
            ['registry', 'Registry', Boxes],
          ] as Array<['git' | 'registry', string, typeof GitBranch]>
        ).map(([wert, beschriftung, Symbol]) => (
          <button
            key={wert}
            onClick={() => setReiter(wert)}
            className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium ${
              reiter === wert
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Symbol size={13} /> {beschriftung}
          </button>
        ))}
      </div>

      {reiter === 'git' ? <GitBereich /> : <RegistryBereich />}
    </div>
  );
}
